/**
 * What a touch on the sheet handle MEANT — a tap, or a swipe, and which way.
 *
 * Pulled out of the component because the bug it exists to prevent is pure
 * arithmetic and the component around it is not testable in this suite
 * (`vite.config.ts` runs vitest in `environment: 'node'`).
 *
 * **The defect this replaces**: the handle carried an `onClick` AND touch
 * handlers, and a mobile browser dispatches a synthetic `click` after
 * `touchend` unless the default is prevented. So a swipe up expanded the sheet,
 * the component re-rendered, and the synthetic click then read the NEW state
 * through the freshly-rendered `onClick` and toggled it straight back — the
 * sheet flashed open and collapsed again. (`touch-action: none` in the CSS does
 * not help: it governs the browser's own pan gesture, not the compatibility
 * click.) The component now decides the whole gesture here and calls
 * `preventDefault()`, so no synthetic click is ever dispatched and `onClick`
 * is left to the mouse and the keyboard alone.
 */

export type SheetState = 'collapsed' | 'expanded';

/** Past this many pixels of vertical travel a touch is a swipe, not a tap. */
export const SWIPE_PX = 40;

/**
 * The sheet state a touch gesture asks for.
 *
 * A swipe is DIRECTIONAL and idempotent — swiping up on an already-expanded
 * sheet leaves it expanded rather than toggling it shut, which is what a
 * direction means. A tap toggles, because a tap has no direction to read.
 */
export function sheetAfterTouch(
  from: number | null,
  to: number | null,
  current: SheetState,
): SheetState {
  if (from === null || to === null) return current;
  const travel = from - to;
  if (travel > SWIPE_PX) return 'expanded';
  if (travel < -SWIPE_PX) return 'collapsed';
  return current === 'expanded' ? 'collapsed' : 'expanded';
}
